///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';

import { Subject } from 'rxjs';

import { DuplicateTypeEnum } from '../duplicate-type-enum';
import { GtthAltitudeModePipe } from '../pipes/altitude-mode.pipe';
import { GtthImportNodesService } from '../../import-nodes.service';
import { GtthHistoryService } from '../../../../logical-topology/engine/history-service';
import { GtthDuplicatedNodeFeatureDto } from '../model/duplicated-node-feature-dto.model';

import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { GtthEntityNameValidation } from '../../../../../shared/directives/validation-functions/entity-name';

import { GtthKeyValueDTO } from '../../../../../generated/shared-types.model';
import { GtthLayerDtoModel } from '../../../../../services/project-socket/dto/gtth-layer-dto.model';
import { NodeFeature } from '../../../../logical-topology/entities/node/node-feature';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'winde-duplicate-node',
    templateUrl: './duplicate-node.component.html',
    styleUrls: ['./duplicate-node.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [GtthAltitudeModePipe],
    standalone: false
})
export class GtthDuplicateNodeComponent implements OnInit, OnDestroy {
    @ViewChild('nameEditorInput') inputElRef: ElementRef;

    @Input() node: GtthDuplicatedNodeFeatureDto;
    public originsToSelect: GtthDuplicatedNodeFeatureDto[];
    public readonly tooltipShowDelay = 2000;
    public name: string;
    public layerIndex: number;
    public latitude: number;
    public longitude: number;
    public height: number;
    public distance: number;
    public blockedByPoleAngle: number;
    public altitudeMode: string;
    public type: DuplicateTypeEnum;
    public typeEnum = DuplicateTypeEnum;
    public isMarkedAsNew: boolean;
    public isMarkedAsDoNotImport: boolean;
    public isMarkedAsUpdateOrigin: boolean;
    public isMarkedAsPotentialDuplicate: boolean;
    public isNameChanged: boolean;
    public isNameEditorActive: boolean;
    public initialNameTooltipText: string;
    public nameEditorFormControl: FormControl;
    public layers: GtthKeyValueDTO[];
    public origin: GtthDuplicatedNodeFeatureDto;
    private readonly FEET_MULTIPLIER = 0.3048;
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();
    private isEditingAllowed: boolean;

    constructor(
        private readonly zone: NgZone,
        private readonly cdRef: ChangeDetectorRef,
        private readonly historyService: GtthHistoryService,
        private readonly altitudeModePipe: GtthAltitudeModePipe,
        private readonly importNodesService: GtthImportNodesService
    ) {}

    @Input() set relatedNodes(nodes: GtthDuplicatedNodeFeatureDto[]) {
        this.originsToSelect = nodes;
        if (!nodes?.length) {
            this.origin = null;
        } else if (!this.origin) {
            this.origin = this.originsToSelect[0];
        }
        this.changeNodeOrigin();
    }

    ngOnInit(): void {
        this.initView();
        this.subscribeToEditingLocker();
        this.subscribeToNameChangedNotifications();
        this.subscribeToTypeChangedNotifications();
        this.layers = [];
        if (this.historyService.logicalTopology.layersModeEnabled) {
            this.layers = this.historyService.logicalTopology.layers.map((layer: GtthLayerDtoModel) => {
                return { key: layer.layerIndex, value: layer.getFullName() } as GtthKeyValueDTO;
            });
        }
    }

    initView() {
        const thisNode = this.node;

        this.isNameChanged = false;
        this.isEditingAllowed = false;
        this.isNameEditorActive = false;

        this.isMarkedAsNew = thisNode.isMarkedAsNew();
        this.isMarkedAsDoNotImport = thisNode.isMarkedAsDoNotImport();
        this.isMarkedAsUpdateOrigin = thisNode.isMarkedAsUpdateOrigin();
        this.isMarkedAsPotentialDuplicate = thisNode.isMarkedAsPotentialDuplicate();

        this.name = thisNode.getName();
        this.type = thisNode.getType();
        this.latitude = thisNode.getLat();
        this.layerIndex = thisNode.getLayerIndex();
        this.longitude = thisNode.getLng();
        this.height = this.convertValue(thisNode.getHeight());
        this.distance = this.convertValue(thisNode.getDistance());
        this.blockedByPoleAngle = thisNode.getBlockedByPoleAngle();
        this.altitudeMode = this.altitudeModePipe.transform(thisNode.getAltMode());
        this.initialNameTooltipText = `Initial name: ${thisNode.getInitialName()}`;
    }

    activateNameEditor() {
        if (!this.isEditingAllowed || this.node.isMarkedAsDoNotImport() || this.node.isMarkedAsPotentialDuplicate()) {
            return;
        }
        this.createEditorControl(this.name);
        this.importNodesService.editingLocker.next(this.node);
    }

    deactivateNameEditor() {
        if (this.isNameEditorActive) {
            this.isNameEditorActive = false;
            this.importNodesService.editingLocker.next(null);
        }
    }

    switchType() {
        const thisNode = this.node;

        if (!this.isEditingAllowed || this.importNodesService.hasDuplicateRelatedToOtherOrigin(thisNode)) {
            return;
        }

        if (thisNode.relatedOrigin) {
            thisNode.switchType(
                this.importNodesService.hasDuplicateRelatedToSameOrigin(thisNode),
                this.importNodesService.hasPotentialDuplicateRelatedToSameOrigin(thisNode)
            );
        } else {
            switch (this.type) {
                case DuplicateTypeEnum.NEW:
                    thisNode.setType(DuplicateTypeEnum.POTENTIAL_DUPLICATE);
                    break;
                case DuplicateTypeEnum.NOT_IMPORT:
                    thisNode.setType(DuplicateTypeEnum.NEW);
                    thisNode.relatedOrigin = null;
                    break;
                case DuplicateTypeEnum.POTENTIAL_DUPLICATE:
                    thisNode.setType(DuplicateTypeEnum.NOT_IMPORT);
                    thisNode.relatedOrigin = null;
                    break;
            }
        }
        this.updateViewAfterChanges();

        if (thisNode.isMarkedAsNew()) {
            const originWithSameName = this.historyService.logicalTopology.getStationByName(this.name);
            if (originWithSameName) {
                this.activateNameEditor();
                return;
            }
        }

        if (thisNode.isMarkedAsUpdateOrigin()) {
            const originWithSameName = this.historyService.logicalTopology.getStationByName(this.name);
            if (originWithSameName && originWithSameName.getId() !== thisNode.getOriginId()) {
                this.activateNameEditor();
                return;
            }
        }

        this.deactivateNameEditor();
        this.importNodesService.nodeTypeSynchronizer.next(thisNode);
    }

    applyNewName() {
        if (this.nameEditorFormControl.invalid) {
            return;
        }

        this.node.setNewName(this.nameEditorFormControl.value);
        this.updateViewAfterChanges();

        this.deactivateNameEditor();
        this.importNodesService.nodeNameSynchronizer.next(this.node);
    }

    layerIndexChanged() {
        this.node.setLayerIndex(this.layerIndex);
    }

    changeNodeOrigin() {
        if (this.origin && this.node.getType() === DuplicateTypeEnum.POTENTIAL_DUPLICATE) {
            this.node.relatedOrigin = NodeFeature.parseGeoJson(this.origin, this.historyService);
        } else {
            this.node.relatedOrigin = null;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private subscribeToEditingLocker() {
        this.importNodesService.editingLocker.pipe(takeUntil(this.destroy$)).subscribe((notifier: GtthDuplicatedNodeFeatureDto) => {
            if (notifier === null) {
                this.isEditingAllowed = true;
            } else {
                const hasTheSameId = notifier.getId() === this.node.getId();
                const hasTheSameType = notifier.getType() === this.node.getType();
                const hasTheSameOrigin =
                    notifier.relatedOrigin && this.node.relatedOrigin && notifier.getOriginId() === this.node.getOriginId();
                this.isEditingAllowed = hasTheSameOrigin && hasTheSameId && hasTheSameType;
            }
        });
    }

    private subscribeToNameChangedNotifications() {
        this.importNodesService.nodeNameSynchronizer.pipe(takeUntil(this.destroy$)).subscribe((notifier: GtthDuplicatedNodeFeatureDto) => {
            const thisNode = this.node;

            if (
                !notifier ||
                notifier.getId() !== thisNode.getId() ||
                (notifier.relatedOrigin && thisNode.relatedOrigin && notifier.getOriginId() === thisNode.getOriginId())
            ) {
                return;
            }

            switch (notifier.getType()) {
                case DuplicateTypeEnum.NEW:
                    thisNode.setName(notifier.getName());
                    thisNode.setType(notifier.getType());
                    break;
                case DuplicateTypeEnum.UPDATE_ORIGIN:
                    thisNode.setName(notifier.getName());
                    thisNode.setType(DuplicateTypeEnum.NOT_IMPORT);
                    break;
            }

            this.updateViewAfterChanges();
        });
    }

    private subscribeToTypeChangedNotifications() {
        this.importNodesService.nodeTypeSynchronizer.pipe(takeUntil(this.destroy$)).subscribe((notifier: GtthDuplicatedNodeFeatureDto) => {
            const thisNode = this.node;

            if (
                !notifier ||
                notifier.getId() !== thisNode.getId() ||
                (notifier.relatedOrigin && thisNode.relatedOrigin && notifier.getOriginId() === thisNode.getOriginId())
            ) {
                return;
            }

            switch (notifier.getType()) {
                case DuplicateTypeEnum.NEW:
                    thisNode.setName(notifier.getName());
                    thisNode.setType(notifier.getType());
                    break;
                case DuplicateTypeEnum.NOT_IMPORT:
                    thisNode.setType(notifier.getType());
                    break;
                case DuplicateTypeEnum.UPDATE_ORIGIN:
                    thisNode.setType(DuplicateTypeEnum.NOT_IMPORT);
                    break;
            }

            this.updateViewAfterChanges();
        });
    }

    private updateViewAfterChanges() {
        const thisNode = this.node;

        this.type = thisNode.getType();
        this.isMarkedAsNew = thisNode.isMarkedAsNew();
        this.isMarkedAsDoNotImport = thisNode.isMarkedAsDoNotImport();
        this.isMarkedAsUpdateOrigin = thisNode.isMarkedAsUpdateOrigin();
        this.isMarkedAsPotentialDuplicate = thisNode.isMarkedAsPotentialDuplicate();

        this.name = thisNode.getName();
        this.isNameChanged = this.name !== thisNode.getInitialName();

        this.cdRef.markForCheck();
    }

    private createEditorControl(name) {
        this.nameEditorFormControl = new FormControl(name, [
            Validators.maxLength(70),
            GtthEntityNameValidation(/[^\-a-zA-Z0-9_!@#$%^&()[\]+={}~'`.]/g),
            Validators.required,
            this.validateName.bind(this)
        ]);
        this.isNameEditorActive = true;
        this.focusNameEditorInput();
    }

    private focusNameEditorInput() {
        setTimeout(() => {
            if (this.inputElRef) {
                this.inputElRef.nativeElement.focus();
            }
        }, 100);
    }

    private validateName(control: AbstractControl): ValidationErrors | null {
        return this.importNodesService.checkIfNameValid(control.value, this.node) ? null : { validationError: true };
    }

    private convertValue(value: number): number {
        return this.historyService.logicalTopology.productUnits === 'feet' ? value / this.FEET_MULTIPLIER : value;
    }
}
